import { getDataOrNull } from "@msys/common";
import { InvitationTokenData } from "./InvitationTokenData.js";
import { RefTokenQuery } from "./InvitationTokenProvider.generated.js";

// `organisationMembershipInvitation` sits behind an extra wrapper object
// (`OrganisationMembershipInvitationQueryResult`) whose non-null `result`
// field is itself the union of the pending invitation and the data-free
// `InvitationNoLongerOpen` member — unwrap both levels here. All invitation
// types share the same `i_<token>` envelope, so the backend resolves
// `InvitationNoLongerOpen` for ANY refToken without a pending membership
// row, even when that token actually belongs to a contractee or CRM linking
// invitation. A genuine sibling match must therefore win over that
// fallback: try the pending membership invitation first, then the
// pending contractee invitation, then the CRM linking invitation, then a
// referral, and only once none of those match does `InvitationNoLongerOpen`
// apply — i.e. a membership or contractee link that is truly spent.
export function resolveInvitationTokenData(
  data: RefTokenQuery | undefined
): InvitationTokenData | null {
  const organisationMembershipInvitationResult = getDataOrNull(
    data?.organisationMembershipInvitation
  )?.result;
  const pendingOrganisationMembershipInvitation =
    organisationMembershipInvitationResult?.__typename ===
    "OrganisationMembershipInvitation"
      ? organisationMembershipInvitationResult
      : null;
  // The contractee lookup matches by token regardless of status, and its
  // page cannot accept a settled invitation — so only a pending one counts.
  const projectContracteeInvitation =
    data?.projectContracteeInvitation?.__typename ===
      "ProjectContracteeInvitation" &&
    data.projectContracteeInvitation.status !== "PENDING"
      ? null
      : data?.projectContracteeInvitation;

  return (
    pendingOrganisationMembershipInvitation ??
    projectContracteeInvitation ??
    data?.crmLinkingInvitation ??
    data?.referralInfo ??
    organisationMembershipInvitationResult ??
    null
  );
}
